import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import { moveItemInArray, CdkDragDrop } from "@angular/cdk/drag-drop";

import { cloneDeep } from "lodash";
import { TranslateService } from "@ngx-translate/core";

import { ColumnHidden, ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";

@Component({
  selector: "table-column-sorter, button[table-column-sorter]",
  templateUrl: "./column-sorter.component.html",
  styleUrls: ["./column-sorter.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnSorterComponent implements OnInit, OnChanges {
  @Input() initDisplayedColumns: string[];
  @Input() displayedColumns: string[];
  @Input() pinnedColumns: string[];
  @Input() columnsHidden: ColumnHidden[];
  @Input() set columnSorterInfo(columnInfo: ColumnSorterInfo[]) {
    if (columnInfo) {
      this.columnInfo = cloneDeep(columnInfo);
      this.emitDisplayedColumns();
      this.emitPinedColumns();
    }
  }

  @Output()
  displayedColumnsChange: EventEmitter<string[]> = new EventEmitter<string[]>(true);
  @Output()
  pinnedColumnsChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output()
  tableConfigSaved: EventEmitter<ColumnSorterInfo[]> = new EventEmitter<
    ColumnSorterInfo[]
  >();

  columnInfo: ColumnSorterInfo[];
  initialColumnInfo: ColumnSorterInfo[];
  showHideAllColsButton = false;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.updateColumns();
  }

  ngOnChanges(changes: SimpleChanges) {
    const { initDisplayedColumns, columnsHidden } = changes;

    if (initDisplayedColumns && columnsHidden) {
      const {
        previousValue: displayedColPrevVal,
        currentValue: displayedColCurVal,
        firstChange: displayedColFirstChange,
      } = initDisplayedColumns;
      const {
        previousValue: colHiddenPrevVal,
        currentValue: colHiddenCurVal,
        firstChange: colHiddenFirstChange,
      } = columnsHidden;

      if (
        JSON.stringify(displayedColPrevVal) !== JSON.stringify(displayedColCurVal) &&
        JSON.stringify(colHiddenPrevVal) !== JSON.stringify(colHiddenCurVal) &&
        !displayedColFirstChange && !colHiddenFirstChange
      ) {
        this.updateColumns();
      }
    }
  }

  updateColumns() {
    this.columnInfo = this.setColumnInfo(this.initDisplayedColumns);
    this.initialColumnInfo = cloneDeep(this.columnInfo);
    this.checkCols();
    this.emitDisplayedColumns();
  }

  setColumnInfo(displayedColumns: string[]) {
    return displayedColumns.map((column, index) => {
      return {
        id: column,
        name: this.columnsHidden[index].header,
        hidden: this.columnsHidden[index].hidden,
        pin: false,
      };
    });
  }

  searchChanged(event) {
    const { value } = event.target;
    this.columnInfo = this.columnInfo.map((col) => {
      const { name } = col;
      return {
        ...col,
        filtered: !this.translate.instant(name).toLowerCase().startsWith(value.toLowerCase())
      }
    });
  }

  columnMenuDropped(event: CdkDragDrop<any>): void {
    const {
      item: {
        data: { columnIndex, columnPinned },
      },
      currentIndex,
    } = event;

    if (
      (columnPinned && currentIndex >= this.pinnedColumns?.length) ||
      (!columnPinned && currentIndex < this.pinnedColumns?.length)
    ) {
      return;
    }

    moveItemInArray(this.columnInfo, columnIndex, currentIndex);
    this.emitDisplayedColumns();
  }

  toggleSelectedColumn(columnId: string) {
    this.columnInfo = this.columnInfo.map((col) => {
      const { id, hidden } = col;
      return {
        ...col,
        hidden: id === columnId ? !hidden : hidden
      }
    });
    this.emitDisplayedColumns();
    this.checkCols();
  }

  selectPinColumn(columnId: string) {
    this.columnInfo = this.columnInfo.map((col) => {
      const { id, pin } = col;
      return {
        ...col,
        pin: id === columnId ? !pin : pin
      }
    });
    this.columnInfo = this.reorganizeList(this.columnInfo);
    this.emitDisplayedColumns();
    this.emitPinedColumns();
  }

  reorganizeList(columnInfo: ColumnSorterInfo[]) {
    return columnInfo
      .filter(({ pin }) => pin)
      .concat(this.columnInfo.filter(({ pin }) => !pin));
  }

  showAllColumns() {
    this.columnInfo = this.columnInfo.map((col) => {
      return {
        ...col,
        hidden: false,
      };
    });
    this.showHideAllColsButton = true;
    this.emitDisplayedColumns();
  }

  hideAllColumns() {
    this.columnInfo = this.columnInfo.map((col) => {
      return {
        ...col,
        hidden: true,
      };
    });
    this.showHideAllColsButton = false;
    this.emitDisplayedColumns();
  }

  restoreDefaultColumns() {
    this.columnInfo = this.initialColumnInfo;
    this.showHideAllColsButton = false;
    this.emitDisplayedColumns();
    this.emitPinedColumns();
  }

  saveTableConfig() {
    this.tableConfigSaved.emit([...this.columnInfo]);
  }

  checkCols() {
    if (this.displayedColumns.length === this.initDisplayedColumns.length - 1) {
      this.showHideAllColsButton = true;
    } else {
      this.showHideAllColsButton = false;
    }
  }

  private emitDisplayedColumns() {
    this.displayedColumnsChange.emit(
      this.columnInfo.filter(({ hidden }) => !hidden).map(({ id }) => id)
    );
  }

  private emitPinedColumns() {
    this.pinnedColumnsChange.emit(
      this.columnInfo.filter(({ pin }) => pin).map(({ id }) => id)
    );
  }
}
